import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setCurrentTab } from '../redux/slices/currentTabSlice';
import { setCurrentPublication } from '../redux/slices/currentPublicationSlice';
import { setTabOrPublicationPage, setIsBrowserNavigation } from '../redux/slices/appSlice';
import { setMobileMenuOpen } from '../redux/slices/mobileMenuSlice';
import { detectPageType } from '../utils/pageTypeDetector';

const SwitchPageUsingBrowserBTNs = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const tabsData = useSelector((state) => state.tabs_data.tabs);
  const sitePath = useSelector((state) => state.app.sitePath);
  const tabOrPublicationPage = useSelector((state) => state.app.tab_or_publication_page);
  const isBrowserNavigation = useSelector((state) => state.app.isBrowserNavigation);
  const currentTab = useSelector((state) => state.currentTab.currentTab);
  const [firstRender, setFirstRender] = React.useState(false);

  // Отслеживаем навигацию через кнопки браузера
  React.useEffect(() => {
    // Отключаем автоматическое восстановление позиции скролла браузером
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const handlePopState = () => {
      dispatch(setIsBrowserNavigation(true));
      dispatch(setMobileMenuOpen(false)); // Закрываем мобильный подхедер при навигации браузера
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch]);

  React.useEffect(() => {
    // Проверяем, что данные загружены
    if (!firstRender) {
      if (!tabsData || tabsData.length === 0) {
        return;
      }

      // Разбиваем sitePath на массив по "/"
      const sitePathArray = sitePath.split('/').filter((item) => item !== '');

      // Получаем mainTabElement
      let mainTabElement = null;
      for (let i = 0; i < tabsData.length; i++) {
        const element = tabsData[i];
        if (element.eng_name === 'main_tab') {
          mainTabElement = element;
          break;
        }
      }

      if (tabOrPublicationPage == 'tab') {
        if (sitePathArray.length === 0) {
          if (mainTabElement) {
            //   'SwitchPageUsingBrowserBTNs: setCurrentTab called with mainTabElement:',
            //   mainTabElement.eng_name,
            // );
            dispatch(setCurrentTab(mainTabElement));
            setFirstRender(true);
          }
        } else if (sitePathArray.length === 1 && /^\d+$/.test(sitePathArray[0])) {
          // Это страница пагинации main_tab: /:number
          if (mainTabElement) {
            dispatch(setCurrentTab(mainTabElement));
            setFirstRender(true);
          }
        } else if (sitePathArray.length === 2 && /^\d+$/.test(sitePathArray[1])) {
          // Это страница пагинации обычного раздела: /section/:number
          tabsData.forEach((element) => {
            if (element.eng_name === sitePathArray[0]) {
              dispatch(setCurrentTab(element));
              setFirstRender(true);
            }
          });
        } else {
          tabsData.forEach((element) => {
            if (element.eng_name === sitePathArray[0]) {
              //   'SwitchPageUsingBrowserBTNs: setCurrentTab called with element:',
              //   element.eng_name,
              // );
              dispatch(setCurrentTab(element));
              setFirstRender(true);
            }
          });
        }
      } else if (tabOrPublicationPage == 'publication') {
        if (sitePathArray.length === 1) {
          let tabElement = mainTabElement;

          if (tabElement && tabElement.publications) {
            tabElement.publications.forEach((element) => {
              if (element.eng_name === sitePathArray[0]) {
                dispatch(setCurrentPublication(element));
                setFirstRender(true);
              }
            });
          }
          // При переключении на публикацию НЕ меняем currentTab, чтобы не менять showName
          // dispatch(setCurrentTab(tabElement));
        } else {
          let tabElement = null;

          tabsData.forEach((element) => {
            if (element.eng_name === sitePathArray[0]) {
              // При переключении на публикацию НЕ меняем currentTab, чтобы не менять showName
              // dispatch(setCurrentTab(element));
              tabElement = element;
            }
          });
          if (tabElement && tabElement.publications) {
            tabElement.publications.forEach((element) => {
              if (element.eng_name === sitePathArray[1]) {
                dispatch(setCurrentPublication(element));
                setFirstRender(true);
              }
            });
          }
        }
      }
    }
  }, [tabOrPublicationPage, tabsData, sitePath, firstRender, dispatch]);

  React.useEffect(() => {
    if (firstRender) {
      if (sitePath != '') {
        if (tabsData && tabsData.length > 0) {
          let mainTabElement = null;
          let foundMatch = false;

          // Разбиваем location.pathname на массив по "/"
          const pathArray = location.pathname.split('/').filter((item) => item !== '');

          const pageType = detectPageType(location.pathname, tabsData);

          if (pageType == 'tab') {
            dispatch(setTabOrPublicationPage('tab'));
          } else if (pageType == 'publication') {
            dispatch(setTabOrPublicationPage('publication'));

            // Получаем данные текущей публикации на основе localPath
            let foundPublication = null;

            if (pathArray.length === 1) {
              // Публикация на главной странице
              const mainTabElement = tabsData.find((element) => element.eng_name === 'main_tab');
              if (mainTabElement && mainTabElement.publications) {
                foundPublication = mainTabElement.publications.find(
                  (pub) => pub.eng_name === pathArray[0],
                );
              }
            } else if (pathArray.length > 1) {
              // Публикация на странице конкретной вкладки
              //   'Searching in tab, pathArray[0]:',
              //   pathArray[0],
              //   'pathArray[1]:',
              //   pathArray[1],
              // );
              const tabElement = tabsData.find((element) => element.eng_name === pathArray[0]);
              if (tabElement && tabElement.publications) {
                foundPublication = tabElement.publications.find(
                  (pub) => pub.eng_name === pathArray[1],
                );
              }
            }

            if (foundPublication) {
              dispatch(setCurrentPublication(foundPublication));
            }
          }
          tabsData.forEach((element) => {
            if (element.eng_name === 'main_tab') {
              mainTabElement = element;
            }
            if ('/' + element.eng_name === '/' + pathArray[0]) {
              // Найдено совпадение
              // Проверяем, не является ли это уже текущей вкладкой
              if (!currentTab || currentTab.eng_name !== element.eng_name) {
                dispatch(setCurrentTab(element));
              }
              foundMatch = true;
            }
          });
          if (!foundMatch) {
            // Проверяем, не является ли это уже текущей вкладкой
            if (!currentTab || currentTab.eng_name !== mainTabElement?.eng_name) {
              dispatch(setCurrentTab(mainTabElement));
            }
          }
        }
      }
    }
  }, [firstRender, location.pathname, sitePath, dispatch, tabsData]);

  // Отдельный useEffect для управления скроллом
  React.useEffect(() => {
    if (!firstRender) return;

    // Прокрутка страницы наверх при обычной навигации (клики по сайту)
    // НЕ прокручиваем при навигации через кнопки браузера
    if (!isBrowserNavigation) {
      window.scrollTo(0, 0);
    } else {
      // Сбрасываем флаг браузерной навигации после небольшой задержки
      // чтобы другие компоненты успели его проверить
      setTimeout(() => {
        dispatch(setIsBrowserNavigation(false));
      }, 100);
    }
  }, [location.pathname, isBrowserNavigation, firstRender, dispatch]);

  return null;
};
export default SwitchPageUsingBrowserBTNs;
